import {areSetsEqual} from '@benchling/js-utils';
import {produce} from 'immer';
import {assign, setup, fromPromise, assertEvent} from 'xstate';

import {ANTIBODY_CONSTANT_DOMAIN_TYPES} from 'antibodies/AntibodyConstants';
import {distinctDomainToKey, formatToSortedDistinctDomains} from 'antibodies/AntibodyUtils';
import {areConstantDomainsFullyDefined} from 'antibodies/nested-data-creation/AntibodyNestedDataCreationUtil';
import {
  AntibodyNestedDataCreationState,
  ConstantAaDomainEntity,
  INITIAL_ANTIBODY_NESTED_DATA_CREATION_STATE,
  TranslationParameters,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {createAntibodiesFromDnaPairs} from 'antibodies/nested-data-creation/NestedDataCreationDAO';
import {assert} from 'Asserts';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

type AntibodyNestedDataCreationModalEvents =
  | {type: 'next'}
  | {type: 'back'}
  | {type: 'selectRegistry'; registryId: string | null}
  | {type: 'selectFormat'; format: AntibodyFormatGraphQL | null}
  | {type: 'setProjectFolder'}
  | {type: 'setTranslationParameters'; translationParameters: TranslationParameters}
  | {type: 'selectConstantDomains'; constantDomainEntities: Record<string, ConstantAaDomainEntity>}
  | {type: 'selectDnaDomainPairs'; pairIds: Array<string>}
  | {type: 'createAndRegister'};

async function handleCreateAntibodies(
  selectedPairIds: Array<string>,
  constantDomainEntities: Array<ConstantAaDomainEntity>,
  folderId: string | null
): Promise<void> {
  assert(
    areConstantDomainsFullyDefined(constantDomainEntities),
    'Expected an entity for every constant domain'
  );
  assert(folderId != null, 'Expected a folder ID');
  try {
    LoadingScreen.show('Creating antibodies from DNA pairs...');
    await createAntibodiesFromDnaPairs(selectedPairIds, constantDomainEntities, folderId);
    notifications.info("We're working on creating antibodies and components...");
  } catch (e) {
    notifications.notifyError(e);
    throw e;
  } finally {
    LoadingScreen.hide();
  }
}

/**
 * State machine handling the progress and state of configuring antibody nested data creation in a modal.
 */
const AntibodyNestedDataCreationModalMachine = setup({
  types: {
    context: {} as AntibodyNestedDataCreationState,
    events: {} as AntibodyNestedDataCreationModalEvents,
  },
  actors: {
    submit: fromPromise(
      async ({
        input,
      }: {
        input: {
          pairIds: Array<string>;
          constantDomainEntities: Array<ConstantAaDomainEntity>;
          folderId: string | null;
        };
      }) => {
        await handleCreateAntibodies(input.pairIds, input.constantDomainEntities, input.folderId);
        return {success: true};
      }
    ),
  },
  actions: {
    selectRegistry: assign(({context, event}) => {
      assertEvent(event, 'selectRegistry');
      return produce(context, (draft) => {
        draft.selectedRegistryId = event.registryId;
      });
    }),
    selectFormat: assign(({context, event}) => {
      assertEvent(event, 'selectFormat');
      const newFormatId = event.format?.id ?? null;
      if (context.selectedFormatId === newFormatId) {
        return context;
      }
      return produce(context, (draft) => {
        draft.selectedFormatId = newFormatId;
        if (event.format != null) {
          // When a format is selected, prepopulate the constant domain entities with all required constant domains for that format
          const constantDomains = Object.values(formatToSortedDistinctDomains(event.format)).filter(
            (domain) => ANTIBODY_CONSTANT_DOMAIN_TYPES.includes(domain.type)
          );
          draft.inputConfig.constantAaDomains.entities = constantDomains.reduce(
            (acc, domain) => {
              acc[distinctDomainToKey(domain)] = {...domain, aaSequenceId: null};
              return acc;
            },
            {} as Record<string, ConstantAaDomainEntity>
          );
        } else {
          draft.inputConfig.constantAaDomains.entities = {};
        }
      });
    }),
    selectDnaDomainPairs: assign(({context, event}) => {
      assertEvent(event, 'selectDnaDomainPairs');
      return produce(context, (draft) => {
        draft.inputConfig.variableDnaDomains.pairIds = event.pairIds;
      });
    }),
    setTranslationParameters: assign(({context, event}) => {
      assertEvent(event, 'setTranslationParameters');
      return produce(context, (draft) => {
        draft.outputConfig.variableAaDomains.translationParameters = event.translationParameters;
      });
    }),
    selectConstantDomains: assign(({context, event}) => {
      assertEvent(event, 'selectConstantDomains');
      // Since selectFormat prepopulates all the constant domains on a format, this assertion ensures
      // that the required constant domains are not changed unexpectedly.
      assert(
        areSetsEqual(
          new Set(Object.keys(event.constantDomainEntities)),
          new Set(Object.keys(context.inputConfig.constantAaDomains.entities))
        ),
        'Number of constant domain entities should not change on selection'
      );
      return produce(context, (draft) => {
        draft.inputConfig.constantAaDomains.entities = event.constantDomainEntities;
      });
    }),
  },
}).createMachine({
  context: () =>
    produce(INITIAL_ANTIBODY_NESTED_DATA_CREATION_STATE, (draft) => {
      draft.outputConfig.antibodies.folderId = FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? null;
    }),
  id: 'antibodyNestedDataCreationModal',
  initial: 'selectFormat',
  states: {
    selectFormat: {
      initial: 'defineCreation',
      on: {
        next: {
          target: 'defineDomains',
          guard: ({context}) => context.selectedRegistryId != null && context.selectedFormatId != null,
        },
      },
      states: {
        defineCreation: {
          on: {
            selectRegistry: {actions: 'selectRegistry'},
            selectFormat: {actions: 'selectFormat'},
          },
        },
      },
    },
    defineDomains: {
      initial: 'defineDomains',
      on: {
        next: {
          target: 'finalize',
          guard: ({context}) =>
            context.inputConfig.variableDnaDomains.pairIds.length > 0 &&
            areConstantDomainsFullyDefined(Object.values(context.inputConfig.constantAaDomains.entities)),
        },
        back: {
          target: 'selectFormat',
        },
      },
      states: {
        defineDomains: {
          on: {
            setTranslationParameters: {actions: 'setTranslationParameters'},
            selectDnaDomainPairs: {actions: 'selectDnaDomainPairs'},
            selectConstantDomains: {actions: 'selectConstantDomains'},
          },
        },
      },
    },
    finalize: {
      initial: 'finalizeStep',
      on: {
        back: {
          target: 'defineDomains',
        },
        createAndRegister: {
          target: 'saving',
        },
      },
      states: {
        finalizeStep: {
          on: {
            setProjectFolder: {
              target: 'finalizeStep',
            },
          },
        },
      },
    },
    saving: {
      invoke: {
        src: 'submit',
        input: ({context}) => ({
          pairIds: context.inputConfig.variableDnaDomains.pairIds,
          constantDomainEntities: Object.values(context.inputConfig.constantAaDomains.entities),
          folderId: context.outputConfig.antibodies.folderId,
        }),
        onDone: {
          target: 'done',
        },
        onError: {
          target: 'finalize',
        },
      },
    },
    done: {
      type: 'final',
    },
  },
});

export default AntibodyNestedDataCreationModalMachine;
